import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { SnippetFolder } from '../lib/types'

const FOLDER_STORAGE_KEY = 'lawyer_snippet_folders'
const SNIPPET_STORAGE_KEY = 'lawyer_saved_snippets'
const DEFAULT_MAIN_FOLDER_ID = 'local_main_folder'
const DEFAULT_FAVORITES_FOLDER_ID = 'local_favorites_folder'
const DEFAULT_MAIN_FOLDER_NAME = 'Main memory'
const DEFAULT_FAVORITES_NAME = 'Favorites'

export interface SnippetContextValue {
  folders: SnippetFolder[]
  loading: boolean
  refreshFolders: () => Promise<void>
  createFolder: (name: string) => Promise<SnippetFolder>
  saveSnippet: (payload: SaveSnippetPayload) => Promise<Snippet>
  toggleFavorite: (snippetId: string, favorite: boolean) => Promise<void>
}

export interface SaveSnippetPayload {
  content: string
  folder_id?: string | null
  source?: Record<string, unknown> | null
  favorite?: boolean
}

export interface Snippet {
  snippet_id: string
  folder_id: string
  content: string
  favorite: boolean
  source?: Record<string, unknown> | null
  created_at: string
}

const safeParse = <T,>(value: string | null, fallback: T): T => {
  if (!value) {
    return fallback
  }
  try {
    return JSON.parse(value) as T
  } catch (err) {
    console.warn('Failed to parse stored snippet data', err)
    return fallback
  }
}

const createDefaultMainFolder = (): SnippetFolder => ({
  folder_id: DEFAULT_MAIN_FOLDER_ID,
  name: DEFAULT_MAIN_FOLDER_NAME,
  is_main: true,
  created_at: new Date().toISOString(),
})

const createDefaultFavoritesFolder = (): SnippetFolder => ({
  folder_id: DEFAULT_FAVORITES_FOLDER_ID,
  name: DEFAULT_FAVORITES_NAME,
  is_main: false,
  created_at: new Date().toISOString(),
})

const ensureDefaultFolders = (folders: SnippetFolder[]): SnippetFolder[] => {
  const hasMain = folders.some((folder) => folder.is_main || folder.name === DEFAULT_MAIN_FOLDER_NAME)
  const hasFavorites = folders.some((folder) => folder.name === DEFAULT_FAVORITES_NAME)
  const next = [...folders]
  if (!hasMain) {
    next.unshift(createDefaultMainFolder())
  }
  if (!hasFavorites) {
    next.push(createDefaultFavoritesFolder())
  }
  return next
}

const readFoldersFromStorage = (): SnippetFolder[] => {
  if (typeof window === 'undefined') {
    return ensureDefaultFolders([])
  }
  const stored = window.localStorage.getItem(FOLDER_STORAGE_KEY)
  const parsed = safeParse<SnippetFolder[]>(stored, [])
  return ensureDefaultFolders(parsed)
}

const readSnippetsFromStorage = (): Snippet[] => {
  if (typeof window === 'undefined') {
    return []
  }
  const stored = window.localStorage.getItem(SNIPPET_STORAGE_KEY)
  return safeParse<Snippet[]>(stored, [])
}

const persistFolders = (folders: SnippetFolder[]) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(FOLDER_STORAGE_KEY, JSON.stringify(folders))
}

const persistSnippets = (snippets: Snippet[]) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(SNIPPET_STORAGE_KEY, JSON.stringify(snippets))
}

const generateId = (prefix: string) => {
  const randomPart =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().split('-').join('')
      : Math.random().toString(36).slice(2)
  return `${prefix}${randomPart}`
}

const SnippetContext = createContext<SnippetContextValue | null>(null)

export function SnippetProvider({ children }: { children: ReactNode }) {
  const [folders, setFolders] = useState<SnippetFolder[]>([])
  const [, setSnippets] = useState<Snippet[]>([])
  const [loading, setLoading] = useState(true)

  const loadStoredData = useCallback(() => {
    const storedFolders = readFoldersFromStorage()
    setFolders(storedFolders)
    persistFolders(storedFolders)
    setSnippets(readSnippetsFromStorage())
    setLoading(false)
  }, [])

  useEffect(() => {
    loadStoredData()
  }, [loadStoredData])

  const refreshFolders = useCallback(async () => {
    setLoading(true)
    const storedFolders = readFoldersFromStorage()
    setFolders(storedFolders)
    persistFolders(storedFolders)
    setLoading(false)
  }, [])

  const createFolder = useCallback(async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) {
      throw new Error('Folder name is required')
    }
    let created: SnippetFolder | undefined
    setFolders((prev) => {
      const existing = prev.find((folder) => folder.name.toLowerCase() === trimmed.toLowerCase())
      if (existing) {
        created = existing
        return prev
      }
      created = {
        folder_id: generateId('sf_'),
        name: trimmed,
        is_main: false,
        created_at: new Date().toISOString(),
      }
      const updated = [...prev, created]
      persistFolders(updated)
      return updated
    })
    if (!created) {
      throw new Error('Unable to create folder')
    }
    return created
  }, [])

  const saveSnippet = useCallback(
    async (payload: SaveSnippetPayload) => {
      const targetFolderId =
        payload.folder_id ??
        folders.find((folder) => folder.is_main || folder.name === DEFAULT_MAIN_FOLDER_NAME)?.folder_id ??
        DEFAULT_MAIN_FOLDER_ID
      const snippet: Snippet = {
        snippet_id: generateId('ss_'),
        folder_id: targetFolderId,
        content: payload.content.trim(),
        favorite: Boolean(payload.favorite),
        source: payload.source ?? null,
        created_at: new Date().toISOString(),
      }
      setSnippets((prev) => {
        const updated = [...prev, snippet]
        persistSnippets(updated)
        return updated
      })
      return snippet
    },
    [folders]
  )

  const toggleFavorite = useCallback(async (snippetId: string, favorite: boolean) => {
    let found = false
    setSnippets((prev) => {
      const updated = prev.map((snippet) => {
        if (snippet.snippet_id !== snippetId) {
          return snippet
        }
        found = true
        return { ...snippet, favorite }
      })
      if (found) {
        persistSnippets(updated)
        return updated
      }
      return prev
    })
    if (!found) {
      throw new Error('Snippet not found')
    }
  }, [])

  return (
    <SnippetContext.Provider
      value={{ folders, loading, refreshFolders, createFolder, saveSnippet, toggleFavorite }}
    >
      {children}
    </SnippetContext.Provider>
  )
}

export function useSnippetContext() {
  const ctx = useContext(SnippetContext)
  if (!ctx) {
    throw new Error('useSnippetContext must be used within SnippetProvider')
  }
  return ctx
}

export { DEFAULT_MAIN_FOLDER_NAME, DEFAULT_FAVORITES_NAME }
